import React, { FC, useState } from 'react'

import { sendPortalVerificationCode, verifyPortalEmailCode } from 'components/contentspace/api'
import Button from 'components/uiKit/Button'
import Input from 'components/uiKit/Input'

import * as s from '../AccessGate.module.scss'

interface IEmailVerificationFormProps {
  companyId: string
  portalId: string
  initialEmail?: string
  onVerified: (email: string) => void
}

export const EmailVerificationForm: FC<IEmailVerificationFormProps> = ({
  companyId,
  portalId,
  initialEmail,
  onVerified,
}) => {
  const [email, setEmail] = useState(initialEmail ?? '')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'EMAIL' | 'CODE'>('EMAIL')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await sendPortalVerificationCode(companyId, email, portalId)
      setStep('CODE')
    } catch {
      setError('Failed to send verification code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const result = await verifyPortalEmailCode(companyId, email, portalId, code)
      if (result.success) {
        onVerified(email)
      } else {
        setError('Invalid code')
      }
    } catch {
      setError('Verification failed. Invalid code or expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {error && <div className={s.error}>{error}</div>}

      {step === 'EMAIL' ? (
        <form onSubmit={handleSendCode}>
          <div className={s.formField}>
            <label className={s.label}>Email</label>
            <Input
              name='verifyEmail'
              onChange={(val) => setEmail(val)}
              type='email'
              value={email}
              required
            />
          </div>
          <div className={s.actions}>
            <Button disabled={!email || loading} name='sendCode' type='submit'>
              {loading ? 'Sending...' : 'Send Code'}
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleVerify}>
          <div className={s.formField}>
            <label className={s.label}>Verification Code</label>
            <Input name='verificationCode' onChange={(val) => setCode(val)} value={code} required />
          </div>

          <div className={s.actions}>
            <Button disabled={!code || loading} name='verifyCode' type='submit'>
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>
            <Button name='backToEmail' onClick={() => setStep('EMAIL')} styleType='secondary'>
              Back
            </Button>
          </div>
        </form>
      )}
    </>
  )
}
